import { action, makeObservable, observable, runInAction } from 'mobx';
import { ILookup, LookupValue, LookupValueSelectListener } from './ILookup';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';
import {
  compareAttributeValue,
  getAttributeValue,
  hasAttributeValue,
  ICfgAttributeValue,
  IComponentProductData,
} from '@canvas-logic/clp-domain';

let id = 0;

// TODO: rewrite
export class AttributeLookup implements ILookup<ICfgAttributeValue> {
  public readonly id = 'attribute-lookup-' + ++id;
  values: LookupValue<ICfgAttributeValue>[] = [];
  current: LookupValue<ICfgAttributeValue> | null = null;
  loading = false;
  private valueSelectListener?: LookupValueSelectListener<ICfgAttributeValue>;
  productData: IComponentProductData[] = [];
  _enable = false;

  constructor(
    private readonly api: ICompositeProductApi,
    public readonly componentKey: string,
    public readonly attributeKey: string,
  ) {
    makeObservable(this, {
      select: action.bound,
      updateValues: action.bound,
      updateCurrent: action.bound,
      values: observable,
      loading: observable,
      current: observable,
    });
    const initialValue = api.getAttributeValue(this.componentKey, this.attributeKey);
    if (initialValue === undefined) {
      this.values = [];
    } else {
      this.values = [
        {
          id: '0',
          value: initialValue,
        },
      ];
    }

    this.productData = this.api.getComponentAllProducts(this.componentKey);
    this._enable = this.api.isEnable(this.componentKey);
    this.updateValues();
  }

  isEnable() {
    return this._enable;
  }

  get componentVariant() {
    return this.api.getComponentVariant(this.componentKey);
  }

  select(id: ICfgAttributeValue) {
    if (id) {
      if (this.api.isAvailable(this.componentKey)) {
        const item = this.values.find((it) => compareAttributeValue(it.value, id));
        if (item === undefined) {
          throw new Error('Unexpected lookup value id');
        }
        const value = item.value;
        this.api
          .setAttributeValue(this.componentKey, this.attributeKey, value)
          .then(() => this.updateValues())
          .then(() => {
            this.updateCurrent();
            if (this.valueSelectListener) {
              this.valueSelectListener(value);
            }
          });
      } else {
        this.api
          .setAvailability(this.componentKey, true)
          .then(() => this.updateValues())
          .then(() => {
            // The original `it.value.value == id.value` leaned on a missing `value`
            // property reading back as `undefined`; narrowing reproduces that exactly.
            const idValue = hasAttributeValue(id) ? id.value : undefined;
            const item = this.values.find(
              (it) => (hasAttributeValue(it.value) ? it.value.value : undefined) == idValue,
            );
            if (item === undefined) {
              throw new Error('Unexpected lookup value id');
            }
            const value = item.value;
            return this.api
              .setAttributeValue(this.componentKey, this.attributeKey, value)
              .then(() => this.updateValues())
              .then(() => {
                this.updateCurrent();
                if (this.valueSelectListener) {
                  this.valueSelectListener(value);
                }
              });
          });
      }
    } else {
      this.api
        .setAvailability(this.componentKey, false)
        .then(() => this.updateValues())
        .then(() => {
          this.updateCurrent();
          if (this.valueSelectListener) {
            this.valueSelectListener('' as any);
          }
        });
    }
  }

  async updateValues() {
    this.loading = true;
    const values = (await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey)) ?? [];

    runInAction(() => {
      this._enable = this.api.isEnable(this.componentKey);
      this.values = values.map((value, index) => ({
        id: index.toString(),
        value: value,
      }));
      this.updateCurrent();
      this.productData = this.api.getComponentAllProducts(this.componentKey);
      this.loading = false;
    });
  }

  setValueSelectListener(listener: LookupValueSelectListener<ICfgAttributeValue>) {
    this.valueSelectListener = listener;
  }

  updateCurrent() {
    if (!this._enable) {
      this.current = null;
      return;
    }
    const selectedValue = this.api.getAttributeValue(this.componentKey, this.attributeKey);
    if (selectedValue === undefined) {
      this.current = null;
      return;
    }
    const value = getAttributeValue(selectedValue);
    const current = this.values.find((it) => hasAttributeValue(it.value) && value === getAttributeValue(it.value));
    if (!current) {
      throw new Error(`Unexpected model property value ${value}. Values: ${this.values.join(',')}`);
    }
    this.current = { ...current };
  }
}
